import { STATUS } from "@/constants/statuses";
import { REQUEST_INTERVAL_MS } from "@apps/rewards/constants/constants";
import { rewardsApplicationLoggerToken } from "@apps/rewards/tokens";
import { applyTransaction } from "@datorama/akita";
import type { Observable } from "rxjs";
import {
    Subscription,
    catchError,
    distinctUntilChanged,
    interval,
    retry,
    switchMap,
    tap,
} from "rxjs";
import { inject, singleton } from "tsyringe";

import type { LoggerInterface } from "@interfaces/LoggerInterface";

import { getLoadingStatus } from "../../../utils/statuses";
import RewardsProgramAccountApiService from "../api_service/RewardsProgramAccountApiService";
import type { ProgramAccountEntity } from "../entities/RewardsProgramAccountEntity";
import RewardsProgramAccountStore from "../store/RewardsProgramAccountStore";
import type { RewardsProgramAccountQueryParams } from "../types";

@singleton()
class RewardsProgramAccountService {
    private subscribersCount = 0;
    private intervalRequestSubscription: Subscription | null = null;

    constructor(
        @inject(rewardsApplicationLoggerToken)
        private readonly logger: LoggerInterface,
        private readonly rewardsProgramAccountApiService: RewardsProgramAccountApiService,
        private readonly rewardsProgramAccountStore: RewardsProgramAccountStore
    ) {}

    public getRewardsProgramAccount(
        queryParams: RewardsProgramAccountQueryParams
    ): Observable<ProgramAccountEntity> {
        this.logger.debug(
            "RewardsProgramAccountService.getRewardsProgramAccount is started"
        );
        const status = this.rewardsProgramAccountStore.getStatus();
        this.rewardsProgramAccountStore.setStatus(getLoadingStatus(status));

        return this.rewardsProgramAccountApiService
            .getRewardsProgramAccount(queryParams)
            .pipe(
                catchError((error: unknown) => {
                    this.logger.debug(
                        "RewardsProgramAccountService.getRewardsProgramAccount is errored",
                        error
                    );
                    this.rewardsProgramAccountStore.setStatus(STATUS.ERROR);
                    throw error;
                }),
                tap((value) => {
                    this.logger.debug(
                        "RewardsProgramAccountService.getRewardsProgramAccount is ready"
                    );
                    //Store Update
                    applyTransaction(() => {
                        this.rewardsProgramAccountStore.update((state) => ({
                            ...state,
                            programAccount: value,
                        }));
                        this.rewardsProgramAccountStore.setStatus(STATUS.READY);
                    });
                })
            );
    }

    private getRewardsProgramAccountWithInterval(
        queryParams: RewardsProgramAccountQueryParams
    ): Observable<ProgramAccountEntity> {
        return interval(REQUEST_INTERVAL_MS).pipe(
            switchMap(() =>
                this.rewardsProgramAccountApiService.getRewardsProgramAccount(
                    queryParams
                )
            ),
            catchError((error: unknown) => {
                this.logger.debug(
                    "RewardsProgramAccountApiService.getRewardsProgramAccountWithInterval is errored",
                    error
                );
                this.rewardsProgramAccountStore.setError(true);
                throw error;
            }),
            distinctUntilChanged(
                (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
            ),
            tap((value) => {
                this.logger.debug(
                    "RewardsProgramAccountApiService.getRewardsProgramAccountWithInterval is ready"
                );
                //Store Update
                this.rewardsProgramAccountStore.update((state) => ({
                    ...state,
                    programAccount: value,
                }));
            }),
            retry({ delay: 1000 })
        );
    }

    public startIntervalRequests = (
        queryParams: RewardsProgramAccountQueryParams
    ) => {
        if (!this.intervalRequestSubscription) {
            this.intervalRequestSubscription =
                this.getRewardsProgramAccountWithInterval(
                    queryParams
                ).subscribe();
        }

        this.subscribersCount++;
    };

    public stopIntervalRequests = () => {
        if (this.intervalRequestSubscription && this.subscribersCount <= 1) {
            this.intervalRequestSubscription.unsubscribe();
            this.intervalRequestSubscription = null;
        }

        this.subscribersCount--;
    };
}

export default RewardsProgramAccountService;
